/**
 * @module useProjectImages
 *
 * React hook for managing project images.
 * Provides fetch, upload, delete, reorder, and cover selection.
 */
import { useState, useEffect, useCallback } from "react";
import {
  fetchProjectImages,
  uploadProjectImage,
  deleteProjectImage,
  reorderProjectImages,
  setCoverImage,
  type ProjectImage,
} from "../lib/projectImages";

export function useProjectImages(projectId: string | undefined) {
  const [images, setImages] = useState<ProjectImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!projectId) {
      setImages([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchProjectImages(projectId);
      setImages(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch images");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const upload = useCallback(
    async (file: File, options?: { isCover?: boolean }) => {
      if (!projectId) throw new Error("No project ID");

      setUploading(true);
      setError(null);

      try {
        const newImage = await uploadProjectImage(projectId, file, options);
        setImages((prev) => [...prev, newImage]);
        return newImage;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to upload image"
        );
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [projectId]
  );

  const uploadMultiple = useCallback(
    async (files: File[]) => {
      if (!projectId) throw new Error("No project ID");

      setUploading(true);
      setError(null);

      const results: ProjectImage[] = [];
      try {
        for (const file of files) {
          const newImage = await uploadProjectImage(projectId, file);
          results.push(newImage);
          setImages((prev) => [...prev, newImage]);
        }
        return results;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to upload images"
        );
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [projectId]
  );

  const remove = useCallback(
    async (imageId: string) => {
      setError(null);
      try {
        await deleteProjectImage(imageId);
        setImages((prev) => prev.filter((img) => img.id !== imageId));
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to delete image"
        );
        throw err;
      }
    },
    []
  );

  const reorder = useCallback(
    async (imageIds: string[]) => {
      setError(null);
      try {
        // Optimistic: reorder locally first
        const reorderedImages = imageIds
          .map((id) => images.find((img) => img.id === id))
          .filter(Boolean) as ProjectImage[];
        setImages(reorderedImages);

        await reorderProjectImages(imageIds);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to reorder images"
        );
        // Refresh to restore actual order
        await refresh();
        throw err;
      }
    },
    [images, refresh]
  );

  const makeCover = useCallback(
    async (imageId: string) => {
      if (!projectId) return;

      setError(null);
      try {
        await setCoverImage(imageId, projectId);
        setImages((prev) =>
          prev.map((img) => ({
            ...img,
            is_cover: img.id === imageId,
          }))
        );
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to set cover image"
        );
        throw err;
      }
    },
    [projectId]
  );

  return {
    images,
    loading,
    uploading,
    error,
    refresh,
    upload,
    uploadMultiple,
    remove,
    reorder,
    makeCover,
  };
}
